import * as assert from 'assert';
import { action, computed, observable, when } from 'mobx';
import { NotificationsConstants } from '../Config/UIConstants';
import { IServerNotification, Notification, NotificationType } from '../Models/Notification';
import { NotificationService } from '../Services/NotificationService';
import { UserStore } from '../_stores/UserStore';
import { IStory, StoryStore } from './StoryStore';
import { IUserFamily, UserFamilyStore } from './UserFamilyStore';

export class NotificationStore {
    private notificationService: NotificationService;
    // private userStore: UserStore;
    private storyStore: StoryStore;
    private familyStore: UserFamilyStore;

    private isAutoRefreshDisabled = false;

    @observable
    private _allNotifications: Array<Notification> = [];
    @computed
    get allNotifications(): Array<Notification> {
        return [...this._allNotifications];
    }

    @observable
    private _recentNotifications: Array<Notification> = [];
    @computed
    public get recentNotifications(): Array<Notification> {
        return [...this._recentNotifications];
    }

    @observable
    private _unviewedNotificationsCount = 0;
    @computed
    public get unviewedNotificationsCount(): number {
        return this._unviewedNotificationsCount;
    }

    @computed
    public get unreadNotificationsCount(): number {
        return this._allNotifications.reduce((count, b) => {
            if (!b.isRead) {
                count++;
            }

            return count;
        }, 0);
    }

    @computed
    public get isReady(): boolean {
        return this.storyStore.storiesAreLoaded && this.familyStore.familiesAreLoaded;
    }

    public constructor(authToken: string, userStore: UserStore, storyStore: StoryStore, familyStore: UserFamilyStore, isAutoRefreshDisabled = false) {
        // this.userStore = userStore;
        this.storyStore = storyStore;
        this.familyStore = familyStore;
        this.isAutoRefreshDisabled = isAutoRefreshDisabled;
        this.notificationService = new NotificationService(authToken);
        when(
            () => this.isReady,
            () => {
                this.refreshAllNotifications();
                if (!this.isAutoRefreshDisabled) {
                    setInterval(this.refreshAllNotifications, 30000);
                }
            }
        );
    }

    @action
    public async refreshUnviewedAndRecent(): Promise<void> {
        await Promise.all([this.refreshRecentNotifications(), this.notificationService.getUnviewedNotificationsCount().then(count => (this._unviewedNotificationsCount = count))]);
    }

    @action
    public refreshAllNotifications = async (): Promise<void> => {
        if (!this.isAutoRefreshDisabled) {
            try {
                const notifications = await this.notificationService.getAllNotifications();
                this._allNotifications = await Promise.all(notifications.map(notification => this.buildSingleNotification(notification)));
            } catch (err) {
                console.log('Error refreshing notifications, disabling refresh: ', err);
                this.isAutoRefreshDisabled = true;
            }
        }
    }

    @action
    public async markNotificationAsRead(notificationID: string): Promise<void> {
        await this.notificationService.setNotificationAsReadByID(notificationID);
        await this.refreshAllNotifications();
        return this.refreshUnviewedAndRecent();
    }

    @action
    public async markAllAsRead(): Promise<void> {
        const firstNotification: Notification | undefined = this._recentNotifications[0];
        if (firstNotification) {
            await this.notificationService.setNotifictionsBeforeIdAsRead(firstNotification.id);
        }
        await this.refreshAllNotifications();
        return this.refreshUnviewedAndRecent();
    }

    @action
    public async viewNotifications(): Promise<void> {
        const firstNotification: Notification | undefined = this._recentNotifications[0];
        if (firstNotification) {
            await this.notificationService.setNotifictionsBeforeIdAsViewed(firstNotification.id);
        }
        await this.refreshAllNotifications();
        return this.refreshUnviewedAndRecent();
    }

    @action
    private async refreshRecentNotifications(): Promise<void> {
        const notifications = await this.notificationService.getRecentNotifications(4);
        this._recentNotifications = await Promise.all(notifications.map(notification => this.buildSingleNotification(notification)));
    }

    private async buildSingleNotification(serverNotification: IServerNotification): Promise<Notification> {
        const text = await this.buildNotificationText(serverNotification);
        return new Notification(serverNotification, text);
    }

    private async buildNotificationText(serverNotification: IServerNotification): Promise<string> {
        let byUser = this.familyStore.getFamilyMemberByID(serverNotification.event.by_user_id);
        if (!byUser) {
            await this.familyStore.loadFamilies();

            byUser = this.familyStore.getFamilyMemberByID(serverNotification.event.by_user_id);
            if (byUser === undefined) {
                return '';
            }
        }
        assert.notEqual(byUser, null, `Null user found for id ${serverNotification.event.by_user_id} in notification id ${serverNotification._id}`);
        let forStory: IStory | undefined;
        let forFamily: IUserFamily | undefined;
        const notificationTextByUser = NotificationsConstants.NOTIFICATION_BODY_TEXT(UserStore.getPublicUserFullName(byUser));
        switch (serverNotification.event.type_id) {
            case NotificationType.PhotoAdded:
                forStory = await this.storyStore.getOrLoadStoryByID(serverNotification.event.collection_id);
                return notificationTextByUser.PHOTO_ADDED(forStory.title, serverNotification.event.photos_added_count!);

            case NotificationType.TextComment:
                forStory = await this.storyStore.getOrLoadStoryByID(serverNotification.event.collection_id);
                return notificationTextByUser.TEXT_COMMENT(forStory.title);

            case NotificationType.AudioComment:
                forStory = await this.storyStore.getOrLoadStoryByID(serverNotification.event.collection_id);
                return notificationTextByUser.AUDIO_COMMENT(forStory.title);

            case NotificationType.StoryShared:
                forStory = await this.storyStore.getOrLoadStoryByID(serverNotification.event.collection_id);
                forFamily = await this.familyStore.getOrLoadFamilyByID(serverNotification.event.family_id);
                return notificationTextByUser.SHARED_STORY(forStory.title, forFamily.name);

            case NotificationType.AddedToFamily:
                forFamily = await this.familyStore.getOrLoadFamilyByID(serverNotification.event.family_id);
                return notificationTextByUser.ADDED_TO_FAMILY(forFamily.name);

            case NotificationType.FamilyNameChange:
                forFamily = await this.familyStore.getOrLoadFamilyByID(serverNotification.event.family_id);
                return notificationTextByUser.FAMILY_NAME_CHANGE(forFamily.name);

            case NotificationType.AcceptedInvite:
                forFamily = await this.familyStore.getOrLoadFamilyByID(serverNotification.event.family_id);
                return notificationTextByUser.ACCEPTED_INVITE(forFamily.name);

            default:
                throw new Error(`Invalid notification type provided to buildNotificationText: ${serverNotification.event!.type_id}`);
        }
    }
}



// WEBPACK FOOTER //
// ./src/Stores/NotificationStore.ts